import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Label, LeftPanel} from '../../Wrappers';
import {setInstructionState} from '../../../redux/features/configSlice';
import Icons from '../../../assets/images';
import {RootState} from '../../../redux/store';
import Api from '../../../utils/api';
import Toaster from '../../Toaster/Index';
import {
    sanitizeAndRedirect,
    sendAmplitudeEvents,
    updatePluginList,
} from '../../../utils/helper';
import {EventNames} from '../../../utils/events';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    Link,
    Text,
    TextInput,
    Radio,
    RadioGroup,
} from '@primer/react';
import { LTButton, LTFlash, LTFormControl, LTLoader, LTModal, LTRadio, LTText } from '@lambdatestincprivate/lt-components';
import LTTextInput from '@lambdatestincprivate/lt-components/lib/components/LTInputBox';
import { InfoIcon } from '@primer/octicons-react';

const modalStyle = document.createElement('style');
modalStyle.innerHTML = '.lt-modal-wrapper span.Dialog__Overlay-sc-13rdxb7-2:before { z-index: 9999 !important; }';

export default function Github() {
    const dispatch = useDispatch();
    const [instanceType, setInstanceType] = useState('OAuth');
    const sideBarObject: any = useSelector(
        (state: RootState) => state.config.sideBarObject
    );
    const [isLoading, setIsLoading] = useState(false);
	const [isApiLoading, setIsApiLoading] = useState(false);
    const [host, setHost] = useState('');
    const [token, setToken] = useState('');
    const [errors, setErrors] = useState({});
	const [isInstallationDone, setIsInstallationDone] = useState(false);
	const [showRevertModal, setShowRevertModal] = useState(false);

	useEffect(() => {
		setIsApiLoading(true);
		async function getStep() {
			try {
				const res = await Api.commonApi.getGithubStep();
				if(res?.data?.step) {
					if(res?.data?.step === "authorization") {
						setIsInstallationDone(true);
						setInstanceType('Repo-based OAuth');
					}
				}
			} catch (err: any) {
				console.error(err);
			}
			setIsApiLoading(false);
		}
		getStep();
	}, [])

	async function setStep(step: string) {
		try {
			await Api.commonApi.setGithubStep(step);
		} catch (err: any) {
			console.error(err);
		}
	}

    const install = async (e: React.ChangeEvent<any>) => {
        e.preventDefault();
        setIsLoading(true);
        setErrors({});
        sendAmplitudeEvents(EventNames.INT_CLICK_INSTALL, {
            category: sideBarObject.category,
            plugin_name: sideBarObject.name,
        });
        try {
            const res = await Api.installApi.commonInstall(sideBarObject.code, {
                host,
                token,
                instance_type: instanceType,
				step: isInstallationDone ? 'authorization' : 'installation'
            });
            if (instanceType === 'OAuth') {
                sanitizeAndRedirect(res.url);
            } else if (instanceType === 'Repo-based OAuth') {
				setIsInstallationDone(true);
				setStep('authorization');
				sanitizeAndRedirect(res.url, true);
				setIsLoading(false);
				return;
			}
            if (res.message) {
                Toaster.success(res.message);
            }
            await updatePluginList();
            setIsLoading(false);
        } catch (err: any) {
            setIsLoading(false);
            if (err.response.data.errors) {
                setErrors(err.response.data.errors);
            } else {
                Toaster.error(err.response.data.message);
            }
        }
    };

	const authorize = async (e: React.ChangeEvent<any>) => {
		e.preventDefault();
        setIsLoading(true);
        setErrors({});
		try {
			const res = await Api.installApi.commonInstall(sideBarObject.code, {
                host,
                token,
                instance_type: instanceType,
				step: 'authorization'
            });
			sanitizeAndRedirect(res.url);
			if (res.message) {
                Toaster.success(res.message);
				// setStep('done');
            }
            await updatePluginList();
            setIsLoading(false);
		} catch (err: any) {
            setIsLoading(false);
            if (err.response.data.errors) {
                setErrors(err.response.data.errors);
            } else {
                Toaster.error(err.response.data.message);
            }
        }
	}

	const goToInstallation = async () => {
		setStep('installation');
		setIsInstallationDone(false);
	}

	useEffect(() => {
		if (showRevertModal) {
			document.getElementsByTagName('head')[0].appendChild(modalStyle);
		} else {
			modalStyle.parentNode?.removeChild(modalStyle);
		}
	})
	
    return (
		<LeftPanel>
			{isApiLoading ? (
				<LTLoader
					heading={`Loading..`}
					size="large"
					containerStyles={{ height: "auto" }}
				/>
			) : (
				<>
					{!isInstallationDone ? (
						<>
							<form
								action=""
								className="flex flex-col space-y-4"
								id="form1"
								onSubmit={install}
							>
								<div
									className="flex flex-col space-y-2"
									role="radiogroup"
									aria-labelledby="instanceTypeLabel"
								>
									<label
										aria-hidden
										id="instanceTypeLabel"
										className={`text-xs font-semibold`}
									>
										Select Instance
									</label>
									<LTRadio
										type="radio"
										checked={instanceType === "OAuth"}
										value={"OAuth"}
										readOnly
										onClick={() => {
											setInstanceType("OAuth");
											dispatch(
												setInstructionState(
													"Instructions"
												)
											);
										}}
										label="OAuth"
										shouldHighlightLabel={true}
									/>
									<LTRadio
										type="radio"
										checked={
											instanceType === "Repo-based OAuth"
										}
										value={"Repo-based OAuth"}
										readOnly
										onClick={() => {
											setInstanceType("Repo-based OAuth");
											dispatch(
												setInstructionState(
													"Instructions"
												)
											);
										}}
										label="Repo-based OAuth"
										shouldHighlightLabel={true}
									/>
									<LTRadio
										type="radio"
										checked={instanceType === "Cloud"}
										value={"Cloud"}
										readOnly
										onClick={() => {
											setInstanceType("Cloud");
											dispatch(
												setInstructionState(
													"Instructions"
												)
											);
										}}
										label="Cloud"
										shouldHighlightLabel={true}
									/>
									<LTRadio
										type="radio"
										checked={instanceType === "SelfHosted"}
										value={"SelfHosted"}
										readOnly
										onClick={() => {
											setInstanceType("SelfHosted");
											dispatch(
												setInstructionState(
													"Instructions(SelfHosted)"
												)
											);
										}}
										label="Self Hosted"
										shouldHighlightLabel={true}
									/>
								</div>
								{instanceType === "SelfHosted" && (
									<LTFormControl
										label="Domain"
										labelStyle={{
											fontSize: 12,
										}}
										formStyles={{
											display: "flex",
											flexDirection: "column",
											gap: "4px",
										}}
									>
										<LTTextInput
											type="text"
											id="url"
											aria-label="Domain"
											width={"100%"}
											contrast
											placeholder="e.g., https://github.mydomain.com"
											value={host}
											onChange={(value: any) =>
												setHost(value)
											}
											trailingAction={() => setHost("")}
											inputOnly={true}
											required
										/>

										{(errors as any).host && (
											<small className="w-full h-2.5 text-[10px] text-red-600">
												{(errors as any).host &&
													(errors as any).host[0]}
											</small>
										)}
									</LTFormControl>
								)}
								{(instanceType === "Cloud" ||
									instanceType === "SelfHosted") && (
									<LTFormControl
										label="API Token"
										labelStyle={{
											fontSize: 12,
										}}
										formStyles={{
											display: "flex",
											flexDirection: "column",
											gap: "4px",
										}}
									>
										<LTTextInput
											type="password"
											aria-label="API Token"
											id="token"
											width={"100%"}
											contrast
											placeholder=""
											onChange={(value: any) =>
												setToken(value)
											}
											trailingAction={() => setToken("")}
											value={token}
											inputOnly={true}
											required
										/>

										{(errors as any).token && (
											<small className="w-full h-2.5 text-[10px] text-red-600">
												{(errors as any).token &&
													(errors as any).token[0]}
											</small>
										)}
									</LTFormControl>
								)}
								<LTButton
									variant={LTButton.Variant.PRIMARY}
									disabled={isLoading}
									onClick={install}
									className="self-start"
								>
									Install
								</LTButton>
							</form>
							<div className="grid inline-block min-[686px]:absolute min-[686px]:bottom-0 max-[685px]:mt-[16px] mb-[16px]">
								<LTText
									color={"muted"}
									style={{
										fontSize: "12px",
										fontWeight: "400",
										lineHeight: "16px",
										color: "#666666",
									}}
								>
									Know how to generate
								</LTText>
								<Link
									className="focus-black"
									href="https://www.lambdatest.com/support/docs/github-integration/"
									target="_blank"
									rel="noreferrer"
								>
									Personal Access Token
								</Link>
							</div>
						</>
					) : (
						<>
							<div
								className="flex flex-col space-y-2"
								role="radiogroup"
								aria-labelledby="instanceTypeLabel"
							>
								<label
									aria-hidden
									id="instanceTypeLabel"
									className={`text-xs font-semibold`}
								>
									Select Instance
								</label>
								<LTRadio
									type="radio"
									checked={instanceType === "OAuth"}
									value={"OAuth"}
									readOnly
									onClick={() => {
										setInstanceType("OAuth");
										dispatch(
											setInstructionState("Instructions")
										);
									}}
									label="OAuth"
									shouldHighlightLabel={true}
									disabled={true}
								/>
								<LTRadio
									type="radio"
									checked={
										instanceType === "Repo-based OAuth"
									}
									value={"Repo-based OAuth"}
									readOnly
									onClick={() => {
										setInstanceType("Repo-based OAuth");
										dispatch(
											setInstructionState("Instructions")
										);
									}}
									label="Repo-based OAuth"
									shouldHighlightLabel={true}
									disabled={true}
								/>
								<LTRadio
									type="radio"
									checked={instanceType === "Cloud"}
									value={"Cloud"}
									readOnly
									onClick={() => {
										setInstanceType("Cloud");
										dispatch(
											setInstructionState("Instructions")
										);
									}}
									label="Cloud"
									shouldHighlightLabel={true}
									disabled={true}
								/>
								<LTRadio
									type="radio"
									checked={instanceType === "SelfHosted"}
									value={"SelfHosted"}
									readOnly
									onClick={() => {
										setInstanceType("SelfHosted");
										dispatch(
											setInstructionState(
												"Instructions(SelfHosted)"
											)
										);
									}}
									label="Self Hosted"
									shouldHighlightLabel={true}
									disabled={true}
								/>
							</div>
							<LTFlash
								// @ts-ignore
								text={
									<div className="flex">
										<InfoIcon
											className="mt-[2px]"
											size={16}
											fill="#0969DA"
										/>
										<div>
											<span
												style={{
													lineHeight: "20px",
												}}
											>
												If your installation request has been approved, you can now proceed to authorize the GitHub app.
											</span>
											<div className="flex mt-[12px] px-[4px]">
												<LTButton
													variant={
														LTButton.Variant.DEFAULT
													}
													disabled={isLoading}
													onClick={authorize}
													className="self-start"
												>
													Authorize
												</LTButton>
												<LTButton
													variant={
														LTButton.Variant
															.INVISIBLE
													}
													disabled={isLoading}
													onClick={() =>
														setShowRevertModal(true)
													}
													className="self-start"
													style={{
														marginLeft: "4px",
													}}
												>
													Revert
												</LTButton>
											</div>
										</div>
									</div>
								}
								extraStyles={{
									marginTop: "16px",
									height: "auto",
									border: "1px solid rgba(84, 174, 255, 0.40)",
									borderRadius: "6",
									boxShadow:
										"0px 1px 0px 0px rgba(0, 0, 0, 0.04)",
								}}
							/>
							<LTModal
								title="Revert Installation"
								isOpen={showRevertModal}
								onClose={() => setShowRevertModal(false)}
								renderFooter={
									// @ts-ignore
									<LTModal.ModalFooter
										footerElements={[
											{
												variant: "default",
												text: "Cancel",
												type: "button",
												size: "medium",
												onClick: () => {
													setShowRevertModal(false);
												},
											},
											{
												variant: "danger",
												text: "Confirm Revert",
												type: "button",
												size: "medium",
												onClick: () => {
													setShowRevertModal(false);
													goToInstallation();
												},
											},
										]}
									/>
								}
							>
								<div className='m-[20px]'>
									Are you sure you want to revert the GitHub Repo-based Installation?
								</div>
							</LTModal>
						</>
					)}
				</>
			)}
		</LeftPanel>
	);
}